/**
 * The runtime's drawing (specs 012, 031): one evaluated frame onto a 2D
 * context. `SceneRuntime.render` calls it for the player; `renderStill`
 * (still.ts) calls it for the assistants' look at the stage, on a Node
 * canvas. It draws only from the `FrameState` (the scene supplies static
 * styling: colours, transition kinds, dialog text) and touches no `window`,
 * `document` or clock, so it runs in the browser and in Node alike.
 */

import type { FrameState, PlayableScene, Transition } from '../@types';
import { containRect } from '../utils/spriteSize';
import { sheetFrameRect } from '../utils/spriteSheet';
import { DIALOG_FONT, dialogBoxText, visibleDialogLines, type DialogBoxRect, type DialogLayoutCache } from './dialogBox';

/**
 * A decoded image the runtime draws: an `<img>` element, or an `ImageBitmap`
 * (`StoryRuntime` keeps one per media id so WebKit cannot drop the decoded
 * pixels and decode again on the main thread mid-animation; spec 020).
 */
export type SceneImage = HTMLImageElement | ImageBitmap;

/** Decoded images by media id (sprite states and background layers). */
export type SceneImages = Record<number, SceneImage>;

/** The bold sans-serif placeholder labels use (spec 026). */
export const PLACEHOLDER_FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/**
 * Draw frame `f` of `scene` onto `ctx` (in stage px, under whatever transform
 * the caller set) and return the dialog boxes it drew, in stage px: one per
 * dialog whose panel is visible.
 */
export function drawFrame(
  ctx: CanvasRenderingContext2D,
  f: FrameState,
  scene: PlayableScene,
  images: SceneImages,
  layouts: DialogLayoutCache,
): DialogBoxRect[] {
  let boxes: DialogBoxRect[] = [];
  const W = f.stage.width;
  const H = f.stage.height;
  const enter = scene.enterTransition;
  const exit = scene.exitTransition;
  const enterHidden = 1 - f.transition.enter;
  const exitHidden = f.transition.exit;

  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = f.background.color;
  ctx.fillRect(0, 0, W, H);

  ctx.save();
  if (enter && enterHidden > 0) applyTransition(ctx, enter, enterHidden, W, H);
  if (exit && exitHidden > 0) applyTransition(ctx, exit, exitHidden, W, H);
  drawBase(ctx, f, images, W, H);
  drawLayers(ctx, f, scene, images, W, H);
  drawSprites(ctx, f, scene, images, W, H);
  boxes = drawDialogs(ctx, f, scene, layouts, W, H);
  ctx.restore();

  if (enter?.type === 'fade' && enterHidden > 0) fadeOverlay(ctx, enterHidden, W, H);
  if (exit?.type === 'fade' && exitHidden > 0) fadeOverlay(ctx, exitHidden, W, H);
  ctx.restore();
  return boxes;
}

/** Slide, zoom and wipe move or clip the stage by the hidden fraction (0 = fully shown). */
function applyTransition(ctx: CanvasRenderingContext2D, transition: Transition, hidden: number, W: number, H: number): void {
  const direction = transition.direction || 'left';
  switch (transition.type) {
    case 'slide': {
      const dx = direction === 'left' ? -W : direction === 'right' ? W : 0;
      const dy = direction === 'up' ? -H : direction === 'down' ? H : 0;
      ctx.translate(dx * hidden, dy * hidden);
      break;
    }
    case 'zoom': {
      const scale = 1 - 0.5 * hidden;
      ctx.globalAlpha = 1 - 0.5 * hidden;
      ctx.translate(W / 2, H / 2);
      ctx.scale(scale, scale);
      ctx.translate(-W / 2, -H / 2);
      break;
    }
    case 'wipe': {
      const shown = 1 - hidden;
      ctx.beginPath();
      if (direction === 'left') ctx.rect(W * hidden, 0, W * shown, H);
      else if (direction === 'right') ctx.rect(0, 0, W * shown, H);
      else if (direction === 'up') ctx.rect(0, H * hidden, W, H * shown);
      else ctx.rect(0, 0, W, H * shown);
      ctx.clip();
      break;
    }
    default:
      break;
  }
}

function fadeOverlay(ctx: CanvasRenderingContext2D, opacity: number, W: number, H: number): void {
  ctx.globalAlpha = 1;
  ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`;
  ctx.fillRect(0, 0, W, H);
}

/**
 * The background's base image cover-fits the stage over the colour and
 * under the layers (spec 026); a background placeholder is a dashed frame
 * inset over the colour with its label in capitals.
 */
function drawBase(ctx: CanvasRenderingContext2D, f: FrameState, images: SceneImages, W: number, H: number): void {
  const change = f.background.change;
  if (!change) {
    drawBasePicture(ctx, f.background.baseMediaId, f.background.placeholder, images, W, H);
    return;
  }
  // A background state change (spec 039 D-4): the "from" base, then the "to" base through the change.
  const p = Math.min(1, Math.max(0, change.progress));
  if (p < 1) drawBasePicture(ctx, change.fromMediaId, change.fromPlaceholder, images, W, H);
  if (p <= 0) return;
  if (p >= 1) {
    drawBasePicture(ctx, f.background.baseMediaId, f.background.placeholder, images, W, H);
    return;
  }
  ctx.save();
  drawBaseChange(ctx, change.type, change.direction, p, W, H);
  drawBasePicture(ctx, f.background.baseMediaId, f.background.placeholder, images, W, H);
  ctx.restore();
}

/** One base picture cover-fitted to the stage, or its placeholder (spec 026). */
function drawBasePicture(
  ctx: CanvasRenderingContext2D, mediaId: number | undefined, placeholder: string | undefined, images: SceneImages, W: number, H: number,
): void {
  const img = mediaId !== undefined ? images[mediaId] : undefined;
  if (img) {
    const { width: iw, height: ih } = naturalSize(img);
    if (iw > 0 && ih > 0) {
      const k = Math.max(W / iw, H / ih);
      ctx.drawImage(img, (W - iw * k) / 2, (H - ih * k) / 2, iw * k, ih * k);
    }
  }
  if (placeholder !== undefined) drawBackgroundPlaceholder(ctx, placeholder, W, H);
}

/**
 * How the "to" base draws at progress `p` of a background change (spec 039
 * D-4), the stage transitions' shapes: `fade` its alpha 0 → 1 over the
 * "from" base; `slide` it moves in from the `direction` edge; `zoom` it
 * grows from scale 0.5 and alpha 0, centred; `wipe` a clip edge reveals it
 * travelling in `direction`. Any other type (a cut) draws it as it is.
 */
export function drawBaseChange(
  ctx: CanvasRenderingContext2D, type: Transition['type'], direction: Transition['direction'], p: number, W: number, H: number,
): void {
  const hidden = 1 - p;
  const d = direction || 'left';
  switch (type) {
    case 'fade':
      ctx.globalAlpha = ctx.globalAlpha * p;
      break;
    case 'slide': {
      const dx = d === 'left' ? -W : d === 'right' ? W : 0;
      const dy = d === 'up' ? -H : d === 'down' ? H : 0;
      ctx.translate(dx * hidden, dy * hidden);
      break;
    }
    case 'zoom': {
      const scale = 0.5 + 0.5 * p;
      ctx.globalAlpha = ctx.globalAlpha * p;
      ctx.translate(W / 2, H / 2);
      ctx.scale(scale, scale);
      ctx.translate(-W / 2, -H / 2);
      break;
    }
    case 'wipe':
      ctx.beginPath();
      if (d === 'left') ctx.rect(W * hidden, 0, W * p, H);
      else if (d === 'right') ctx.rect(0, 0, W * p, H);
      else if (d === 'up') ctx.rect(0, H * hidden, W, H * p);
      else ctx.rect(0, 0, W, H * p);
      ctx.clip();
      break;
    default:
      break;
  }
}

/** Layers cover-fit the stage, or tile when their translate loops. */
function drawLayers(ctx: CanvasRenderingContext2D, f: FrameState, scene: PlayableScene, images: SceneImages, W: number, H: number): void {
  const base = ctx.globalAlpha;
  for (const layer of f.background.layers) {
    const img = images[layer.mediaId];
    if (!img) continue;
    const source = scene.background.layers?.find((l) => l.id === layer.id);
    const tiled = !!(source?.animation?.translate?.enabled && source.animation.translate.loop);
    ctx.save();
    ctx.globalAlpha = base * layer.opacity;
    ctx.translate(W / 2, H / 2);
    ctx.rotate((layer.rotation * Math.PI) / 180);
    ctx.scale(layer.scale, layer.scale);
    ctx.translate(-W / 2, -H / 2);
    if (tiled) {
      const pattern = ctx.createPattern(img, 'repeat');
      if (pattern) {
        ctx.translate(layer.offsetX, layer.offsetY);
        ctx.fillStyle = pattern;
        ctx.fillRect(-layer.offsetX - W, -layer.offsetY - H, W * 3, H * 3);
      }
    } else {
      const { width: iw, height: ih } = naturalSize(img);
      if (iw > 0 && ih > 0) {
        const k = Math.max(W / iw, H / ih);
        const dw = iw * k;
        const dh = ih * k;
        ctx.drawImage(img, (W - dw) / 2 + layer.offsetX, (H - dh) / 2 + layer.offsetY, dw, dh);
      }
    }
    ctx.restore();
  }
}

/**
 * Each sprite's image is contain-fitted in its box, placed at the hinge, then
 * rotated and scaled, then mirrored around the box centre when flipped (spec
 * 038). A sprite showing a spritesheet frame (spec 033) draws
 * only that frame's rectangle, contain-fitted by the frame's size, without
 * smoothing for pixel art.
 */
function drawSprites(ctx: CanvasRenderingContext2D, f: FrameState, scene: PlayableScene, images: SceneImages, W: number, H: number): void {
  const base = ctx.globalAlpha;
  for (const s of f.sprites) {
    if (!(s.opacity > 0)) continue;
    const img = s.mediaId === null ? undefined : images[s.mediaId];
    // A picture that is set but not loaded draws nothing; no picture at all draws the placeholder (spec 026).
    if (!img && s.placeholder === undefined) continue;
    ctx.save();
    ctx.globalAlpha = base * Math.min(1, s.opacity);
    ctx.translate((s.x / 100) * W, (s.y / 100) * H);
    ctx.rotate((s.rotation * Math.PI) / 180);
    ctx.scale(s.scale, s.scale);
    // A flip mirrors the picture inside its box around the box centre (spec
    // 038 D-1); an unflipped sprite adds no transform. The placeholder is
    // drawn in place, so its label stays readable.
    const flipped = (s.flipX || s.flipY) && !!img;
    if (flipped) {
      const cx = (0.5 - s.hingeX) * s.width;
      const cy = (0.5 - s.hingeY) * s.height;
      ctx.translate(cx, cy);
      ctx.scale(s.flipX ? -1 : 1, s.flipY ? -1 : 1);
      ctx.translate(-cx, -cy);
    }
    const cut = img && s.sheet && s.mediaId !== null ? scene.sheets?.[s.mediaId] : undefined;
    if (img && cut && s.sheet) {
      const r = sheetFrameRect(cut, s.sheet.index, naturalSize(img));
      if (r.w > 0 && r.h > 0) {
        const fit = containRect({ width: s.width, height: s.height }, { width: r.w, height: r.h });
        const smoothing = ctx.imageSmoothingEnabled;
        if (s.sheet.pixelArt) ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, r.x, r.y, r.w, r.h, -s.hingeX * s.width + fit.x, -s.hingeY * s.height + fit.y, fit.width, fit.height);
        if (s.sheet.pixelArt) ctx.imageSmoothingEnabled = smoothing;
      }
    } else if (img) {
      const fit = containRect({ width: s.width, height: s.height }, naturalSize(img));
      ctx.drawImage(img, -s.hingeX * s.width + fit.x, -s.hingeY * s.height + fit.y, fit.width, fit.height);
    } else {
      drawSpritePlaceholder(ctx, s.placeholder!, -s.hingeX * s.width, -s.hingeY * s.height, s.width, s.height);
    }
    ctx.restore();
  }
}

/**
 * Dialog panels and text, laid out by `layoutDialogBox` (through the given
 * layout cache) from the line's whole text (so typing reveals within the final layout and the box does not grow
 * while it types); one `fillText` (and `strokeText` with an outline) per line.
 */
function drawDialogs(ctx: CanvasRenderingContext2D, f: FrameState, scene: PlayableScene, layouts: DialogLayoutCache, W: number, H: number): DialogBoxRect[] {
  const base = ctx.globalAlpha;
  const boxes: DialogBoxRect[] = [];
  // The font is set before measuring, so `measureText` measures in DIALOG_FONT.
  const measure = (s: string) => ctx.measureText(s).width;
  for (const d of f.dialogs) {
    const dialog = scene.dialogs.find((x) => x.id === d.id);
    const line = dialog?.lines.find((l) => l.id === d.lineId);
    ctx.save();
    ctx.font = DIALOG_FONT;
    const box = layouts.layout(dialog ? dialogBoxText(dialog, d.lineId, f.t) : '', d.position, { width: W, height: H }, measure);
    boxes.push({ id: d.id, position: d.position, x: box.x, y: box.y, width: box.width, height: box.height });
    ctx.globalAlpha = base * d.panelOpacity;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(box.x, box.y, box.width, box.height);
    if (dialog && line && d.text && d.textOpacity > 0) {
      // Styling follows the line, then the dialog.
      const outlineColor = line.outlineColor || dialog.outlineColor;
      const outlineWidth = line.outlineWidth ?? dialog.outlineWidth ?? 2;
      ctx.globalAlpha = base * d.textOpacity;
      ctx.font = DIALOG_FONT;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const shown = visibleDialogLines(box.lines, d.text, box.cut);
      const firstY = box.y + box.height / 2 - ((box.lines.length - 1) * box.lineHeight) / 2;
      shown.forEach((text, i) => {
        if (!text) return;
        const y = firstY + i * box.lineHeight;
        if (outlineColor) {
          ctx.strokeStyle = outlineColor;
          ctx.lineWidth = outlineWidth * 2;
          ctx.strokeText(text, W / 2, y);
        }
        ctx.fillStyle = line.textColor || dialog.textColor || '#ffffff';
        ctx.fillText(text, W / 2, y);
      });
    }
    ctx.restore();
  }
  return boxes;
}

/** `text` cut with "…" so it measures at most `max` wide. */
function fitText(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let cut = text;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > max) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
}

/**
 * A sprite placeholder filling its size box at (x, y): a dark fill, a dashed
 * outline, a head-and-shoulders silhouette and the label in capitals near
 * the bottom, in a bold sans-serif sized to the box (at least 10 px).
 */
function drawSpritePlaceholder(ctx: CanvasRenderingContext2D, label: string, x: number, y: number, w: number, h: number): void {
  if (!(w > 0) || !(h > 0)) return;
  ctx.fillStyle = 'rgba(20, 22, 30, 0.82)';
  ctx.fillRect(x, y, w, h);
  // Silhouette: a head and shoulders, centred in the upper part of the box.
  const cx = x + w / 2;
  const unit = Math.min(w, h * 0.8);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
  ctx.beginPath();
  ctx.arc(cx, y + h * 0.34, unit * 0.16, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx, y + h * 0.66, unit * 0.32, unit * 0.2, 0, Math.PI, 0);
  ctx.fill();
  // Dashed outline.
  const lw = Math.max(1, Math.min(w, h) * 0.02);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.lineWidth = lw;
  ctx.setLineDash?.([lw * 4, lw * 3]);
  ctx.strokeRect(x + lw / 2, y + lw / 2, w - lw, h - lw);
  ctx.setLineDash?.([]);
  // The label.
  const size = Math.max(10, Math.round(Math.min(h * 0.09, w * 0.11)));
  ctx.font = `bold ${size}px ${PLACEHOLDER_FONT_FAMILY}`;
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(fitText(ctx, label.toUpperCase(), w * 0.9), cx, y + h - Math.max(size, h * 0.1));
}

/** A background placeholder: a dashed frame inset over the colour, its label in capitals in the middle. */
function drawBackgroundPlaceholder(ctx: CanvasRenderingContext2D, label: string, W: number, H: number): void {
  const inset = Math.round(Math.min(W, H) * 0.06);
  const lw = Math.max(1.5, Math.min(W, H) * 0.006);
  ctx.save();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
  ctx.lineWidth = lw;
  ctx.setLineDash?.([lw * 5, lw * 4]);
  ctx.strokeRect(inset, inset, W - inset * 2, H - inset * 2);
  ctx.setLineDash?.([]);
  const size = Math.max(12, Math.round(Math.min(W, H) * 0.05));
  ctx.font = `bold ${size}px ${PLACEHOLDER_FONT_FAMILY}`;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(fitText(ctx, label.toUpperCase(), W - inset * 4), W / 2, H / 2);
  ctx.restore();
}

/** An image's natural size (its decoded size), falling back to its element size; a bitmap's own size. */
export function naturalSize(img: SceneImage): { width: number; height: number } {
  const el = img as Partial<HTMLImageElement>;
  return { width: el.naturalWidth || img.width || 0, height: el.naturalHeight || img.height || 0 };
}
